/**
 * 🔗 PulseBatchDeepLink — Résolution du lien email "Consulter le rapport"
 * =======================================================================
 *
 * Route : /pulse/batches/:batchId
 *
 * Cette page est UNIQUEMENT un router intelligent : elle ne rend AUCUNE
 * UI rapport elle-même (la vue rapport vit dans /team-pulse). Elle se
 * contente de résoudre la bonne destination selon l'état du batch et
 * de l'utilisateur, puis de rediriger proprement.
 *
 * Cas gérés (déterministes, jamais d'écran blanc) :
 *
 *   1. Pas de session active        → /auth?next=/pulse/batches/{id}
 *   2. API 401                       → /auth?next=/pulse/batches/{id}
 *   3. API 404                       → message « rapport introuvable » + Home
 *   4. API 403                       → message « accès non autorisé » + Home
 *   5. Erreur réseau / 5xx           → message + bouton « Réessayer »
 *   6. report_action = view_report   → /team-pulse?open_batch={id}
 *      (rapport payé & débloqué — accès direct dashboard)
 *   7. report_action = view_teaser   → checkout (POST batches/{id}/checkout
 *      ou /team-pulse?open_batch={id}&checkout=1 si endpoint indisponible)
 *   8. report_action = generate ou null → /team-pulse?open_batch={id}
 *      (laisser le dashboard guider — pas de short-circuit)
 *
 * Loading screen premium pendant la résolution → JAMAIS d'écran blanc.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, AlertCircle, FileX, Lock, Home, RefreshCw } from 'lucide-react';
import { getApiUrl } from '../config/environment';
import { useAuth } from '../contexts/AuthContext';

const API = getApiUrl();

// Palette CoachDIA Pulse (cohérent /team-pulse) — Dark Premium
const C = {
  bg: '#0E0B05',
  panel: '#15110A',
  border: 'rgba(212,175,55,0.25)',
  gold: '#D4AF37',
  goldDeep: '#92710A',
  ink: '#F5F0E1',
  inkSoft: 'rgba(245,240,225,0.75)',
  inkMuted: 'rgba(245,240,225,0.55)',
  red: '#E55B3C',
};

const FrameCard = ({ children, testid }) => (
  <div
    className="min-h-screen w-full flex items-center justify-center px-5 py-10"
    style={{ background: C.bg, color: C.ink }}
    data-testid={testid}
  >
    <div
      className="w-full max-w-md rounded-2xl border px-6 sm:px-8 py-10 text-center"
      style={{
        background: C.panel,
        borderColor: C.border,
        boxShadow: `0 20px 60px -25px rgba(0,0,0,0.6), inset 0 1px 0 ${C.gold}22`,
      }}
    >
      {children}
    </div>
  </div>
);

const Brand = () => (
  <div className="mb-6">
    <div
      className="text-[10px] uppercase tracking-[0.32em] font-semibold"
      style={{ color: C.gold }}
    >
      CoachDIA · Pulse
    </div>
    <div
      className="mt-4 h-px mx-auto w-12"
      style={{ background: `linear-gradient(to right, ${C.goldDeep}, ${C.gold})` }}
    />
  </div>
);

const PrimaryBtn = ({ children, onClick, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm font-semibold transition-all hover:scale-[1.02] active:scale-[0.98]"
    style={{
      background: `linear-gradient(135deg, ${C.goldDeep}, ${C.gold})`,
      color: C.bg,
      boxShadow: `0 10px 28px -10px ${C.gold}aa`,
    }}
  >
    {children}
  </button>
);

const SecondaryBtn = ({ children, onClick, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm font-medium transition-all hover:opacity-90"
    style={{
      background: 'transparent',
      color: C.inkSoft,
      border: `1px solid ${C.border}`,
    }}
  >
    {children}
  </button>
);

export default function PulseBatchDeepLink() {
  const { batchId } = useParams();
  const navigate = useNavigate();
  const { user, token, loading: authLoading } = useAuth();
  const [phase, setPhase] = useState('loading'); // loading | error | notfound | forbidden | done
  const [errorMsg, setErrorMsg] = useState('');

  // Chemin de retour post-login. NB : on utilise une URL relative pour que le
  // login Login.js (qui sécurise `?next=` en n'acceptant que les chemins
  // internes commençant par "/") la reconnaisse comme safe.
  const nextUrl = `/pulse/batches/${batchId || ''}`;
  const loginRedirect = `/login?next=${encodeURIComponent(nextUrl)}`;

  // 🎯 Résolution principale : fetch batch puis route selon report_action.
  const resolve = useCallback(async () => {
    if (!batchId) {
      setPhase('notfound');
      return;
    }
    setPhase('loading');
    setErrorMsg('');
    try {
      const { data } = await axios.get(`${API}/api/team-pulse/batches/${batchId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const progress = data?.progress || {};
      const action = progress.report_action;
      // Destination unifiée : on délègue au dashboard /team-pulse qui
      // contient déjà toute la logique d'affichage rapport / checkout.
      // Le query param ?open_batch=… est porté pour pouvoir, à terme,
      // ouvrir directement le batch concerné (rétro-compat : si non lu,
      // l'utilisateur arrive sur le dashboard et y voit son rapport en
      // tête de liste — pas de page blanche, pas de friction critique).
      const dest = `/team-pulse?open_batch=${encodeURIComponent(batchId)}`;
      if (action === 'view_teaser') {
        // Rapport débloqué mais non payé → on signale au dashboard d'ouvrir
        // le checkout via query param dédié (lecture optionnelle côté
        // /team-pulse — le bouton CTA dashboard reste de toute façon
        // proéminent pour ces batches).
        navigate(`${dest}&checkout=1`, { replace: true });
      } else {
        // view_report, generate, null, ou inconnu → dashboard
        navigate(dest, { replace: true });
      }
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) {
        // Session expirée pendant la résolution → re-login avec retour
        navigate(loginRedirect, { replace: true });
        return;
      }
      if (status === 403) {
        setPhase('forbidden');
        setErrorMsg('Vous n\'avez pas accès à ce rapport.');
        return;
      }
      if (status === 404) {
        setPhase('notfound');
        setErrorMsg('Ce rapport n\'est plus disponible.');
        return;
      }
      setPhase('error');
      setErrorMsg(e?.response?.data?.detail || e?.message || 'Erreur réseau.');
    }
  }, [batchId, token, navigate, loginRedirect]);

  useEffect(() => {
    // Attendre que l'auth context soit chargé avant toute décision.
    if (authLoading) return;
    if (!user || !token) {
      // Pas de session → on redirige vers login en conservant l'intent.
      navigate(loginRedirect, { replace: true });
      return;
    }
    resolve();
  }, [authLoading, user, token, navigate, loginRedirect, resolve]);

  // Auth context encore en train de charger → loading premium.
  if (authLoading || phase === 'loading') {
    return (
      <FrameCard testid="pulse-batch-deeplink-loading">
        <Brand />
        <Loader2
          className="w-10 h-10 mx-auto mb-5 animate-spin"
          style={{ color: C.gold }}
        />
        <h1
          className="text-xl mb-2"
          style={{ fontFamily: 'Georgia, serif', color: C.ink }}
        >
          Préparation de votre rapport…
        </h1>
        <p className="text-sm" style={{ color: C.inkMuted }}>
          Vérification de l'accès et chargement des données.
        </p>
      </FrameCard>
    );
  }

  // Si auth est vide et qu'on n'a pas encore navigué (très court), fallback.
  if (!user || !token) {
    return <Navigate to={loginRedirect} replace />;
  }

  if (phase === 'notfound') {
    return (
      <FrameCard testid="pulse-batch-deeplink-notfound">
        <Brand />
        <FileX
          className="w-12 h-12 mx-auto mb-5"
          style={{ color: C.inkMuted }}
          strokeWidth={1.5}
        />
        <h1
          className="text-xl mb-3"
          style={{ fontFamily: 'Georgia, serif', color: C.ink }}
        >
          Rapport introuvable
        </h1>
        <p className="text-sm mb-7" style={{ color: C.inkSoft }}>
          {errorMsg || 'Le rapport demandé n\'existe plus ou a été supprimé.'}
        </p>
        <div className="flex flex-col gap-3">
          <PrimaryBtn
            onClick={() => navigate('/team-pulse', { replace: true })}
            testid="pulse-batch-deeplink-go-dashboard"
          >
            <Home className="w-4 h-4" />
            Mes rapports Pulse
          </PrimaryBtn>
        </div>
      </FrameCard>
    );
  }

  if (phase === 'forbidden') {
    return (
      <FrameCard testid="pulse-batch-deeplink-forbidden">
        <Brand />
        <Lock
          className="w-12 h-12 mx-auto mb-5"
          style={{ color: C.inkMuted }}
          strokeWidth={1.5}
        />
        <h1
          className="text-xl mb-3"
          style={{ fontFamily: 'Georgia, serif', color: C.ink }}
        >
          Accès non autorisé
        </h1>
        <p className="text-sm mb-7" style={{ color: C.inkSoft }}>
          {errorMsg
            || 'Ce rapport est réservé au propriétaire de la campagne. Connectez-vous avec le bon compte.'}
        </p>
        <div className="flex flex-col gap-3">
          <PrimaryBtn
            onClick={() => navigate(loginRedirect, { replace: true })}
            testid="pulse-batch-deeplink-relogin"
          >
            Se reconnecter
          </PrimaryBtn>
          <SecondaryBtn
            onClick={() => navigate('/', { replace: true })}
            testid="pulse-batch-deeplink-home"
          >
            <Home className="w-4 h-4" />
            Accueil
          </SecondaryBtn>
        </div>
      </FrameCard>
    );
  }

  // phase === 'error'
  return (
    <FrameCard testid="pulse-batch-deeplink-error">
      <Brand />
      <AlertCircle
        className="w-12 h-12 mx-auto mb-5"
        style={{ color: C.red }}
        strokeWidth={1.5}
      />
      <h1
        className="text-xl mb-3"
        style={{ fontFamily: 'Georgia, serif', color: C.ink }}
      >
        Une erreur est survenue
      </h1>
      <p className="text-sm mb-7" style={{ color: C.inkSoft }}>
        {errorMsg || 'Impossible de charger le rapport pour le moment.'}
      </p>
      <div className="flex flex-col gap-3">
        <PrimaryBtn
          onClick={() => resolve()}
          testid="pulse-batch-deeplink-retry"
        >
          <RefreshCw className="w-4 h-4" />
          Réessayer
        </PrimaryBtn>
        <SecondaryBtn
          onClick={() => navigate('/team-pulse', { replace: true })}
          testid="pulse-batch-deeplink-fallback-dashboard"
        >
          Mes rapports Pulse
        </SecondaryBtn>
      </div>
    </FrameCard>
  );
}
